import { Box } from "@mui/material";

import { formatCarbonFootprint, formatPercentage } from "@/lib/formatter";

import { Icon } from "@/modules/common/components/Icon";
import { Tag } from "@/modules/common/components/Tag";
import { TagNumber } from "@/modules/common/components/TagNumber";
import { Typography } from "@/modules/common/components/Typography";
import { CardStyled } from "@/modules/play/Components/Synthesis/Synthesis.common.styles";
import { useTeamValuesForTeam } from "@/modules/play/context/hooks/shared";
import { synthesisConstants } from "@/modules/play/playerActions/constants/synthesis";
import { useTranslation } from "@/modules/translations";
import { ITeam } from "@/utils/types";

const CARBON_FOOTPRINT_TONS_THRESHOLD = 2;

export default SynthesisCarbon;

function SynthesisCarbon({ team }: { team: ITeam | null }) {
  const { t } = useTranslation();
  const { teamValues } = useTeamValuesForTeam({ teamId: team?.id });
  const teamCarbonFootprintInKgPerDay = teamValues?.carbonFootprint || 0;
  const carbonFootprintReduction = teamValues?.carbonFootprintReduction || 0;

  const teamCarbonFootprintInTonPerYear =
    teamCarbonFootprintInKgPerDay *
    synthesisConstants.DAYS_IN_YEAR *
    synthesisConstants.KG_TO_TON;

  return (
    <CardStyled>
      <Box display="flex" alignItems="center" gap={1}>
        <Icon name="carbon-footprint" />
        <Typography variant="h4">
          {t("synthesis.player.general-section.carbon-footprint.title")}
        </Typography>
      </Box>

      <Box>
        <Typography
          dangerouslySetInnerHTML={{
            __html: t(
              "synthesis.player.general-section.carbon-footprint.final-mean-carbon-footprint",
              {
                finalFootprint: t("unit.carbon-per-year-per-person.mega", {
                  value: formatCarbonFootprint(teamCarbonFootprintInTonPerYear),
                  count: Math.ceil(teamCarbonFootprintInTonPerYear),
                }),
                footprintReduction: t("unit.percentage", {
                  value: formatPercentage(carbonFootprintReduction),
                }),
              },
            ),
          }}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Tag
          type={
            teamCarbonFootprintInTonPerYear <= CARBON_FOOTPRINT_TONS_THRESHOLD
              ? "success"
              : "error"
          }
        >
          {t("unit.carbon-per-year-per-person.mega", {
            value: formatCarbonFootprint(teamCarbonFootprintInTonPerYear),
            count: Math.ceil(teamCarbonFootprintInTonPerYear),
          })}
        </Tag>
        <TagNumber
          value={-carbonFootprintReduction}
          formatter={(value) =>
            t("unit.percentage", {
              value: formatPercentage(value),
            })
          }
          successDirection="decrease"
        />
      </Box>
    </CardStyled>
  );
}
